/**
 * PendingOrdersTable — Directive 3 (TiF Router).
 * Lists resting LIMIT/STOP buy entries (GTC/DAY) with a cancel action.
 */
import { useEffect, useState, useCallback } from "react";
import { api, fmtKES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, Clock } from "lucide-react";
import { toast } from "sonner";

export function PendingOrdersTable() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        api.get("/orders/pending", { params: { status: "pending" } })
            .then((r) => setOrders(r.data?.orders || []))
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [load]);

    const cancel = async (id) => {
        try {
            await api.delete(`/orders/pending/${id}`);
            toast.success("Pending order cancelled");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not cancel order");
        }
    };

    if (loading || orders.length === 0) return null;

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="pending-orders-table">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                <h3 className="font-bold text-sm">Pending Orders</h3>
                <span className="text-xs text-muted-foreground">({orders.length})</span>
            </div>
            <div className="divide-y divide-border">
                {orders.map((o) => (
                    <div key={o.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`pending-order-row-${o.id}`}>
                        <div className="min-w-0">
                            <div className="font-mono font-bold text-sm">
                                {o.order_type.toUpperCase()} BUY {o.qty} {o.symbol}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Trigger @ {fmtKES(o.trigger_price)} · TIF: {o.tif}
                            </div>
                        </div>
                        <Button
                            variant="ghost" size="sm"
                            className="h-11 w-11 min-h-[44px] min-w-[44px] p-0 text-destructive hover:text-destructive"
                            onClick={() => cancel(o.id)}
                            data-testid={`cancel-pending-order-${o.id}`}
                            aria-label="Cancel pending order"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default PendingOrdersTable;
